import * as React from "react";
import {
  Label,
  AutocompleteSelect,
  cn,
  Spinner,
  Icon,
  type AutocompleteOption,
} from "@ogt/ui";

export interface SearchOption {
  id: string;
  label: string;
  value: string;
  type?: string;
  group?: string;
}

interface MapSearchInputProps {
  value: string;
  options: SearchOption[];
  placeholder?: string;
  onInputChange: (value: string) => void;
  onSelect: (option: SearchOption) => void;
  label: string;
  loading?: boolean;
  emptyContent?: React.ReactNode;
  loadingContent?: React.ReactNode;
  minCharsToOpen?: number;
  closeOnSelect?: boolean;
  openOnFocus?: boolean;
  className?: string;
  dropdownWidthClassName?: string;

  manual?: boolean;
  submitSignal?: string | number | boolean;
  onDraftChange?: (value: string) => void;
}

function getSearchIcon(type?: string) {
  switch (type) {
    case "Hållplats":
      return "pin";

    case "Linje":
      return "busfront";

    case "Linjenät":
      return "map";

    default:
      return "search";
  }
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const cleanQuery = query.trim();

  if (!cleanQuery) return <>{text}</>;

  const index = text.toLowerCase().indexOf(cleanQuery.toLowerCase());

  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}

      <strong>{text.slice(index, index + cleanQuery.length)}</strong>

      {text.slice(index + cleanQuery.length)}
    </>
  );
}

function normalizeSearch(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/\s+/g, " ");
}

function damerauLevenshtein(a: string, b: string) {
  const rows = a.length + 1;
  const cols = b.length + 1;

  const distances = Array.from(
    { length: rows },
    () => Array(cols).fill(0) as number[]
  );

  for (let row = 0; row < rows; row++) {
    distances[row][0] = row;
  }

  for (let column = 0; column < cols; column++) {
    distances[0][column] = column;
  }

  for (let row = 1; row < rows; row++) {
    for (let column = 1; column < cols; column++) {
      const cost = a[row - 1] === b[column - 1] ? 0 : 1;

      distances[row][column] = Math.min(
        distances[row - 1][column] + 1,
        distances[row][column - 1] + 1,
        distances[row - 1][column - 1] + cost
      );

      const isTransposition =
        row > 1 &&
        column > 1 &&
        a[row - 1] === b[column - 2] &&
        a[row - 2] === b[column - 1];

      if (isTransposition) {
        distances[row][column] = Math.min(
          distances[row][column],
          distances[row - 2][column - 2] + 1
        );
      }
    }
  }

  return distances[a.length][b.length];
}

function fuzzyStopMatch(option: SearchOption, query: string) {
  const normalizedQuery = normalizeSearch(query);

  if (normalizedQuery.length < 4) return false;
  if (option.type !== "Hållplats") return false;

  const normalizedText = normalizeSearch(`${option.label} ${option.value}`);

  if (normalizedText.includes(normalizedQuery)) {
    return false;
  }

  const words = normalizedText.split(/[\s,()/-]+/).filter(Boolean);

  return words.some((word) => {
    if (word.length < normalizedQuery.length - 1) {
      return false;
    }

    const candidate = word.slice(0, normalizedQuery.length);

    return damerauLevenshtein(normalizedQuery, candidate) <= 2;
  });
}

const MapSearchInput: React.FC<MapSearchInputProps> = ({
  value,
  options,
  placeholder,
  onInputChange,
  onSelect,
  label,
  minCharsToOpen = 1,
  closeOnSelect = true,
  openOnFocus = true,
  className,
  loading,
  emptyContent,
  loadingContent,
  dropdownWidthClassName,
  manual = false,
  submitSignal,
  onDraftChange,
}) => {
  const [selectedId, setSelectedId] = React.useState<string>();

  const [draft, setDraft] = React.useState(value ?? "");

  React.useEffect(() => {
    if (!manual) return;

    setDraft(value ?? "");
  }, [value, manual]);

  React.useEffect(() => {
    if (!manual) return;

    onInputChange(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitSignal]);

  const queryProp = manual ? draft : value;

  React.useEffect(() => {
    if (!queryProp.trim()) {
      setSelectedId(undefined);
    }
  }, [queryProp]);

  const fuzzyIds = React.useMemo(() => {
    const query = queryProp.trim();

    if (!query) {
      return new Set<string>();
    }

    return new Set(
      options
        .filter((option) => fuzzyStopMatch(option, query))
        .map((option) => option.id)
    );
  }, [options, queryProp]);

  const acOptions = React.useMemo<AutocompleteOption[]>(
    () =>
      options.map((option) => ({
        value: option.id,
        label: option.label,
        keywords: `${option.value} ${option.label}`,
        group: option.group,

        leading: option.type ? (
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted"
            title={option.type}
          >
            <Icon
              name={getSearchIcon(option.type)}
              aria-hidden
              className="size-4"
            />
          </span>
        ) : undefined,
      })),
    [options]
  );

  const filterAutocompleteOption = React.useCallback(
    (option: AutocompleteOption, query: string) => {
      const normalizedQuery = normalizeSearch(query);

      if (!normalizedQuery) return true;

      const label = typeof option.label === "string" ? option.label : "";

      const normalizedOption = normalizeSearch(
        `${label} ${option.keywords ?? ""}`
      );

      return (
        normalizedOption.includes(normalizedQuery) || fuzzyIds.has(option.value)
      );
    },
    [fuzzyIds]
  );

  function handleQueryChange(next: string) {
    if (manual) {
      setDraft(next);
      onDraftChange?.(next);
    } else {
      onInputChange(next);
    }
  }

  function handleValueChange(next?: string) {
    setSelectedId(next);

    if (!next) return;

    const selectedOption = options.find((option) => option.id === next);

    if (!selectedOption) return;

    if (manual) {
      setDraft(selectedOption.label);
      onDraftChange?.(selectedOption.label);
    } else {
      onInputChange(selectedOption.label);
    }

    onSelect(selectedOption);
  }

  const finalLoadingContent = loadingContent ?? (
    <div className="flex items-center gap-2 px-3 py-2">
      <Spinner className="h-4 w-4" />
      <span>Söker…</span>
    </div>
  );

  const finalEmptyContent = emptyContent ?? (
    <div className="px-3 py-2">
      {queryProp && !loading
        ? "Inga träffar på din sökning."
        : "Börja skriva för att söka."}
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label>{label}</Label>

      <AutocompleteSelect
        query={queryProp}
        onQueryChange={handleQueryChange}
        value={selectedId}
        onValueChange={handleValueChange}
        options={acOptions}
        placeholder={placeholder}
        openOnFocus={openOnFocus}
        closeOnSelect={closeOnSelect}
        minCharsToOpen={minCharsToOpen}
        dropdownWidthClassName={dropdownWidthClassName}
        loading={loading}
        emptyContent={finalEmptyContent}
        loadingContent={finalLoadingContent}
        filterFn={filterAutocompleteOption}
        renderOption={(option) => {
          const displayLabel =
            typeof option.label === "string" ? option.label : "";

          return (
            <div className="flex items-center gap-2">
              <span className="truncate">
                <HighlightMatch text={displayLabel} query={queryProp} />
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};

export default MapSearchInput;
